import type { Student, User } from '../types';

/**
 * Why the signed-in session cannot proceed into the app (null: it can).
 *
 * The parent portal lets in only logins the school invited (owner, 28 Oct 2026): the API's
 * GET /Parent/portal answers with the children, or refuses with one of these reasons.
 *  NOT_INVITED     - no account here, or the school never invited this login;
 *  ACCESS_OFF      - invited, but staff switched its parent-portal access off;
 *  NO_CHILDREN     - none of its pupils is linked to it and at the school any more;
 *  ME_FAILED       - /User/me failed otherwise, so access flags are unknown;
 *  CHILDREN_FAILED - /Parent/portal failed otherwise, so the children list is unknown.
 * Every one renders the Access Denied screen.
 */
export type AuthError =
  | 'NOT_INVITED'
  | 'ACCESS_OFF'
  | 'NO_CHILDREN'
  | 'ME_FAILED'
  | 'CHILDREN_FAILED'
  | null;

export type Denial = Exclude<AuthError, null>;

/** /User/me answered and refused. 401/404: the API has no active account for this sign-in. */
export function meRefusal(err: any): Denial {
  const status = err?.response?.status;
  return status === 401 || status === 404 ? 'NOT_INVITED' : 'ME_FAILED';
}

/** /Parent/portal refused: the reason it gives, else what its status means. */
export function childrenRefusal(err: any): Denial {
  const reason = err?.response?.data?.reason;
  if (reason === 'NOT_INVITED' || reason === 'ACCESS_OFF' || reason === 'NO_CHILDREN') return reason;
  switch (err?.response?.status) {
    case 403: return 'ACCESS_OFF';
    case 404: return 'NO_CHILDREN';
    default: return 'CHILDREN_FAILED';
  }
}

/**
 * Whether a signed-in session is turned away, and why. Both navigators gate on this, and so
 * does the live connection: a parent who is turned away receives nothing either.
 */
export function deniedReason(
  authError: AuthError,
  user: User | null,
  students: Student[],
  platform: 'web' | 'native' = 'web',
): Denial | null {
  if (authError) return authError;
  // /User/me explicitly reporting this platform's access off blocks the app entirely,
  // regardless of role/permissions. Web access gates the browser only and mobile access
  // the phone app, as the API does (ParentController.MyKid).
  if (platform === 'web' ? user?.hasWebAccess === false : user?.hasMobileAccess === false) return 'ACCESS_OFF';
  // A parent app with zero children has nothing to show: say that instead of a blank shell.
  if (students.length === 0) return 'NO_CHILDREN';
  return null;
}

export interface DeniedCopy {
  emoji?: string;
  title: string;
  subtitle: string;
  hint: string;
}

const COULD_NOT_CONFIRM: DeniedCopy = {
  title: 'Access Denied',
  subtitle: "We couldn't confirm your access to the parent portal.",
  hint: 'Please try again later. If this keeps happening, contact the school office.',
};

/** What the Access Denied screen says for each reason. */
export const ACCESS_DENIED_COPY: Record<Denial, DeniedCopy> = {
  NOT_INVITED: {
    title: 'Access Denied',
    subtitle: 'Your email address has not been invited to the parent portal.',
    hint: 'Only parents invited by the school can sign in. Please contact the school office to be invited, then sign in with that email address.',
  },
  ACCESS_OFF: {
    title: 'Access Denied',
    subtitle: 'Parent portal access for this login has been switched off.',
    hint: 'Please contact the school office if you believe this is a mistake.',
  },
  NO_CHILDREN: {
    emoji: '🎒',
    title: 'No Student Linked',
    subtitle: 'There is no student linked to your account yet.',
    hint: 'Please contact the school office to link your child to this account.',
  },
  ME_FAILED: COULD_NOT_CONFIRM,
  CHILDREN_FAILED: COULD_NOT_CONFIRM,
};
